import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfig } from '@/src/config/context';
import { Box } from '@mui/material';
import { useVaultSession } from '@/src/state/session';
import { fetchMarketplaceAgent } from '@/src/marketplace/client';
import type { MarketplaceAgentManifest } from '@/src/marketplace/types';
import { InstallAgentDialog } from '@/src/components/scopes/InstallAgentDialog';

type Status = 'loading' | 'picker' | 'not-found' | 'error';

// vault-sdk 3.0.0's `agents.connect` resolves the manifest itself from
// `agentId` (vault-api reads it from the DDC registry) — only `agentId` +
// an explicit `agentServicePubkey` override are sent, no more `version`/
// `manifestCid`. The `manifestCid` presence check stays as a sanity check
// that the marketplace registration is complete.
function toConnectInput(m: MarketplaceAgentManifest): { agentId: string; agentServicePubkey: string } {
  if (!m.manifestCid || !m.agentServicePubkey) {
    throw new Error('Marketplace returned an incomplete manifest (missing manifestCid or agentServicePubkey)');
  }
  return {
    agentId: m.agentId,
    agentServicePubkey: m.agentServicePubkey,
  };
}

export function InstallRoute() {
  const { agentId } = useParams<{ agentId: string }>();
  const session = useVaultSession();
  const config = useConfig();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<MarketplaceAgentManifest | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!agentId) return;
      try {
        if (session.agents.some((a) => a.agentId === agentId)) {
          navigate(`/agents/${agentId}`, { replace: true });
          return;
        }
        // The MARKETPLACE base, not the vault base: vault-api serves no
        // marketplace routes (its auth-then-route order turns the request
        // into a 401 for every anonymous caller), and every sibling call
        // site — connect.tsx, manifest-cards.ts — already passes the
        // marketplace origin. Found on the first human walkthrough of the
        // org install deep link.
        const m = await fetchMarketplaceAgent(config.marketplaceBaseUrl, agentId);
        if (cancelled) return;
        if (!m) { setStatus('not-found'); return; }
        setManifest(m);
        if (session.scopes.length <= 1) {
          const scopeName = session.scopes[0]?.name ?? 'default';
          await (await session.signingHandle()).agents.connect({ ...toConnectInput(m), scope: scopeName, settings: {} });
          await session.refreshAgents();
          navigate(`/agents/${agentId}`, { replace: true });
        } else {
          setStatus('picker');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  async function handleConnect(scope: string) {
    if (!manifest || !agentId) return;
    await (await session.signingHandle()).agents.connect({ ...toConnectInput(manifest), scope, settings: {} });
    await session.refreshAgents();
    navigate(`/agents/${agentId}`, { replace: true });
  }

  if (status === 'not-found') {
    return <Box sx={{ p: 4 }}>Agent not found.</Box>;
  }
  if (status === 'error') {
    return <Box sx={{ p: 4, color: 'error.main' }}>{error}</Box>;
  }
  if (status === 'picker' && manifest) {
    return (
      <InstallAgentDialog
        open
        agent={manifest}
        scopes={session.scopes}
        onConnect={handleConnect}
        onClose={() => navigate('/', { replace: true })}
      />
    );
  }
  return <Box sx={{ p: 4 }}>Connecting...</Box>;
}
